/**
 * Forma de exibir N categorias de um Parceiro (tasks 6.1/6.3).
 *
 * O Parceiro passou a ocupar mais de uma categoria, e a api entrega
 * `partner_categories: PartnerCategory[]` na ordem de `partner_category_ids`
 * — a ordem que o Produtor salvou. Ordem e peso entre categorias estão fora
 * de escopo no PRD, então nada é reordenado aqui.
 *
 * ── A conta de largura, a 375px (piso mobile-first, ACCESSIBILITY.md §12) ──
 *
 * Card da listagem (`features/partners/Card.jsx`):
 *
 *     375  viewport
 *     −24  Screen.Content `px-3`
 *     −2   borda do Card
 *     −16  Card.Body `p-2` (o `md:p-3` só vale a partir de 768px)
 *     ───
 *     333  linha flex
 *     −107 logo `h-20 aspect-[4/3]` → 80 × 4/3
 *     −70  rail direito `w-[70px]`
 *     −16  dois `gap-2`
 *     ───
 *     140  coluna do meio
 *
 * O rótulo é `.badge` (10px, CAIXA ALTA, 700, `letter-spacing: .05em`):
 * ~7,5px por caractere, ou **~18 caracteres** em 140px. "PATROCINADORES" já
 * come 105px; dois rótulos e um separador pedem ~218px. Não cabe.
 *
 * Quebrar linha também não resolve: o ACCESSIBILITY.md §2.4 exige 32
 * caracteres por linha em qualquer bloco de texto que quebre, e 140px dão 18.
 * A mesma seção diz que abaixo de 640px uma linha com texto admite **um**
 * elemento fixo ao lado — o card já tem dois (logo e rail), e só passa porque
 * o rótulo é de uma linha que não quebra. Fazê-lo quebrar o põe dentro da
 * regra que ele hoje não viola.
 *
 * Daí a forma: **um rótulo e um contador** no card. Sobra 140 − 15 ("+2" em
 * dois glifos) − 4 (gap) = ~121px, ou ~16 caracteres, para o primeiro rótulo,
 * que trunca com reticências em vez de transbordar (o `.badge` é `nowrap` e
 * hoje transborda sem aviso). O contador não diz quais são as outras, então
 * a lista inteira vai no nome acessível do card, não só na tela.
 *
 * Capa premium (`premium/PartnerCover.jsx`): a capa tem 132px de altura e o
 * selo começa em `top-4`, deixando 116px. Cada pílula tem ~26px (12px de
 * texto + `py-1`) e o gap é de 4px: três pílulas ocupam 86px e cabem
 * empilhadas à direita; a quarta encostaria no `PartnerIdentityCard`, que
 * sobe `-mt-8` por cima da capa. Por isso o call-site pede `limit = 3`.
 */

export type PartnerCategoryLike = {
  id?: number | string | null
  name?: string | null
} | null

/** Uma categoria por rótulo, aparada, na ordem da api, sem repetir rótulo. */
export const partnerCategoryNames = (
  categories: (PartnerCategoryLike | undefined)[] | null | undefined
): string[] => {
  const seen = new Set<string>()
  const names: string[] = []

  ;(categories ?? []).forEach(category => {
    const name = category?.name?.trim()

    if (!name || seen.has(name)) return

    seen.add(name)
    names.push(name)
  })

  return names
}

/** O card mostra um rótulo; a capa premium mostra três. */
export const COMPACT_CATEGORY_LIMIT = 1

export type CompactPartnerCategories = {
  /** Lista inteira — vai para o nome acessível, não só para a tela. */
  names: string[]
  /** O que cabe na forma decidida acima. */
  visible: string[]
  /** Quantos ficaram de fora. `0` não vira "+0": o call-site não o renderiza. */
  overflowCount: number
}

export const compactPartnerCategories = (
  categories: (PartnerCategoryLike | undefined)[] | null | undefined,
  limit: number = COMPACT_CATEGORY_LIMIT
): CompactPartnerCategories => {
  const names = partnerCategoryNames(categories)

  return {
    names,
    visible: names.slice(0, limit),
    overflowCount: Math.max(names.length - limit, 0)
  }
}

/**
 * Forma compacta com orçamento em **caixas**, não em rótulos.
 *
 * O contador ocupa uma caixa como qualquer pílula, e ignorar isso foi um
 * defeito real: com três pílulas mais o contador, a capa premium renderizava
 * quatro caixas de ~26px e três gaps de 4px — 116px, exatamente o vão útil —,
 * e a última caía nos 32px que o `PartnerIdentityCard` cobre com `-mt-8`.
 * Como o contêiner da capa tem `z-index: auto`, o `z-10` dela pintava por
 * cima do cartão branco.
 *
 * Aqui `boxes` é quantas caixas cabem na tela, e a conta se ajusta: havendo
 * excedente, sobra uma pílula a menos para o contador.
 */
export const boxedPartnerCategories = (
  categories: (PartnerCategoryLike | undefined)[] | null | undefined,
  boxes: number
): CompactPartnerCategories => {
  const names = partnerCategoryNames(categories)
  const limit = names.length > boxes ? boxes - 1 : boxes

  return {
    names,
    visible: names.slice(0, limit),
    overflowCount: Math.max(names.length - limit, 0)
  }
}
